import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { useWebSocket } from '../hooks/useWebSocket';
import IoTDeviceCard from '../components/IoTDeviceCard';
import {
  Activity, Wifi, WifiOff, RefreshCw, Plus, Settings,
  TrendingUp, Zap, AlertTriangle, CheckCircle
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

const IoTMonitoring = () => {
  const [devices, setDevices] = useState([]);
  const [deviceMetrics, setDeviceMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // CRUD states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingDevice, setDeletingDevice] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  const [formData, setFormData] = useState({
    device_number: '',
  });

  // WebSocket connection - use useMemo to keep sessionId stable
  const sessionId = React.useMemo(() => 
    `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    [] // Empty deps array means this only runs once
  );
  const { isConnected } = useWebSocket(sessionId);

  // Fetch devices list
  const fetchDevices = useCallback(async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/iot/devices/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDevices(data || []);
      } else {
        setError('Failed to load devices');
      }
    } catch (err) {
      console.error('Error fetching devices:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchDevices();
  };

  // CRUD Operations
  const handleAddDevice = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/iot/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          device_number: formData.device_number,
        })
      });

      if (response.ok) {
        setShowAddModal(false);
        setFormData({
          device_number: ''
        });
        fetchDevices(); // Refresh devices list
        alert('Device added successfully!');
      } else {
        const data = await response.json();
        alert(`Failed to add device: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error adding device:', err);
      alert('Failed to add device. Please try again.');
    }
  };

  const handleDeleteDevice = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/iot/devices/${deletingDevice.device_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setDeletingDevice(null);
        fetchDevices();
        alert('Device deleted successfully!');
      } else {
        const data = await response.json();
        alert(`Failed to delete device: ${data.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error deleting device:', err);
      alert('Failed to delete device. Please try again.');
    }
  };

  const openDeleteModal = (device) => {
    setDeletingDevice(device);
    setShowDeleteModal(true);
  };

  const getConnectionColor = (status) => {
    const colors = {
      online: 'bg-green-500',
      offline: 'bg-red-500',
      idle: 'bg-yellow-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const formatTimestamp = (isoString) => {
    if (isoString == null) return 'N/A';
    const date = new Date(isoString);
    const formatter = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC',
      hour12: false
    });

    return formatter.format(date).replace(',', '');
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading Monitoring devices...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Device Monitoring</h1>
            <p className="text-gray-600">Real-time device monitoring and analytics</p>
          </div>
          <div className="flex items-center gap-3">            
            <button
              onClick={handleRefresh}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
            
            <button 
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Device</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search devices by ID or name..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>

          {/* Status Filter */}
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="maintenance">Maintenance</option>
            <option value="faulty">Faulty</option>
          </select>

          {/* Sort */}
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={filters.sort_by}
            onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}
          >
            <option value="created_at">Date Created</option>
            <option value="device_name">Device Name</option>
            <option value="status">Status</option>
          </select>
        </div>
      </div>

      {/* Devices Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
            <p className="mt-2 text-gray-500">No devices found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Serial Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    API Token
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Connection Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Online
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {devices.map((iotDevice) => (
                  <tr key={iotDevice.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-3`}></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{iotDevice.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 text-nowrap">{iotDevice.device.device_number || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 text-nowrap">{iotDevice.device.device_name || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 text-nowrap">{iotDevice.device.serial_number || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{iotDevice.api_token || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full text-white ${getConnectionColor(iotDevice.connection_status_computed || 'offline')}`}>
                        {iotDevice.connection_status_computed}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{formatTimestamp(iotDevice.last_online)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Device Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Add New Monitoring Device</h2>
            <form onSubmit={handleAddDevice}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Device Number *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.device_number}
                    onChange={(e) => setFormData({...formData, device_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., DEV-D803811851"
                  />
                  <p className="text-xs text-gray-500 mt-1">Unique identifier from device label</p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData({
                      device_number: ''
                    });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Device
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold mb-4 text-red-600">Delete Device</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete device <strong>{deletingDevice?.device_name}</strong> ({deletingDevice?.device_id})? 
              This action cannot be undone and will remove all associated data.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingDevice(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteDevice}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Device
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default IoTMonitoring;
